import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { useRouter } from 'next/router';
import * as tsClient from '@tsClient';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import ListAndDetailsSubLayout from '@layouts/ListAndDetailsSubLayout';
import TeamsContext from '@providers/Teams/TeamsContext';
import useNoodleApi from '@hooks/useNoodleApi';
import useIsMobile, { SIZES } from '@hooks/useIsMobile';
import Plus from '@components/Icons/Plus';
import CreateContactModal from '@/modals/CreateContactModal';
import CreateCompanyModal from '@/modals/CreateCompanyModal';

const PAGE_SIZE = 25;

enum Tabs {
  Contacts = 'contacts',
  Companies = 'companies',
}

type FilterType = 'all' | 'subscribed_members' | 'free_members' | 'unverified_contacts';

const CONTACT_FILTERS: Array<{ key: FilterType; label: string }> = [
  {
    key: 'all',
    label: 'All',
  },
  {
    key: 'subscribed_members',
    label: 'Subscribed',
  },
  {
    key: 'free_members',
    label: 'Free',
  },
  {
    key: 'unverified_contacts',
    label: 'Unverified',
  },
];

const Members: React.FC = ({ children }) => {
  const pathParams = useParams<{ id?: string }>();
  const router = useRouter();

  const isOneColumn = useIsMobile(SIZES.md);
  const { currentTeamOwner } = useContext(TeamsContext);

  const [searchTerms, setSearchTerms] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] = useState(false);
  const [isCreateCompanyModalOpen, setIsCreateCompanyModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<Tabs>(Tabs.Contacts);
  const [members, setMembers] = useState<Awaited<ReturnType<typeof tsClient.getMembers>>['items']>([]);
  const [numMembers, setNumMembers] = useState<number | undefined>(undefined);
  const [companies, setCompanies] = useState<Awaited<ReturnType<typeof tsClient.getCompanies>>['items']>([]);
  const [numCompanies, setNumCompanies] = useState<number | undefined>(undefined);
  const { getData: getMembersFn } = useNoodleApi(tsClient.getMembers);
  const { getData: getCompaniesFn } = useNoodleApi(tsClient.getCompanies);
  const [key, setKey] = useState(0);

  const creatorSlug = currentTeamOwner?.slug;
  const creatorId = currentTeamOwner?.id;
  const selectedId = pathParams?.id;

  const loadMembersPage = useCallback(
    async ({ page, search, filter }: { page: number; search?: string; filter: FilterType }): Promise<void> => {
      setCurrentPage(page);
      if (creatorSlug) {
        setIsFetching(true);
        const getResponse = await getMembersFn({
          creatorSlug,
          filter,
          page,
          perPage: PAGE_SIZE,
          search,
        });
        if (getResponse.data) {
          setMembers(getResponse.data.items);
          setNumPages(getResponse.data.numPages);
          setNumMembers(getResponse.data.numItems);
        }
        setIsFetching(false);
      }
    },
    [creatorSlug, getMembersFn],
  );

  const loadCompaniesPage = useCallback(
    async ({ page, search }: { page: number; search?: string }): Promise<void> => {
      setCurrentPage(page);

      if (creatorId) {
        setIsFetching(true);

        const getResponse = await getCompaniesFn({
          creatorId,
          page,
          perPage: PAGE_SIZE,
          search,
        });

        if (getResponse.data) {
          setCompanies(getResponse.data.items);
          setNumCompanies(getResponse.data.numItems);
        }

        setIsFetching(false);
      }
    },
    [creatorId, getCompaniesFn],
  );

  const handlePageChange = (newPage: number): void => {
    if (selectedTab === Tabs.Contacts) {
      loadMembersPage({
        filter: selectedFilter,
        page: newPage,
        search: searchTerms ?? undefined,
      });
    } else {
      loadCompaniesPage({
        page: newPage,
        search: searchTerms ?? undefined,
      });
    }
  };

  useEffect(() => {
    if (!creatorId || !creatorSlug) return;

    if (selectedTab === Tabs.Contacts && !isCreateContactModalOpen) {
      loadMembersPage({
        filter: selectedFilter,
        page: 1,
        search: searchTerms ?? undefined,
      });
    } else if (selectedTab === Tabs.Companies && !isCreateCompanyModalOpen) {
      loadCompaniesPage({
        page: 1,
        search: searchTerms ?? undefined,
      });
    }
  }, [
    selectedTab,
    loadMembersPage,
    loadCompaniesPage,
    creatorSlug,
    creatorId,
    searchTerms,
    selectedFilter,
    isCreateContactModalOpen,
    isCreateCompanyModalOpen,
  ]);

  useEffect(() => {
    const currentPath = router.pathname;

    if (currentPath.startsWith('/dashboard/members')) {
      setSearchTerms(null);
      setSelectedTab(Tabs.Contacts);
    } else if (currentPath.startsWith('/dashboard/companies')) {
      setSearchTerms(null);
      setSelectedTab(Tabs.Companies);
    }
  }, [router.pathname]);

  const items = useMemo<Parameters<typeof ListAndDetailsSubLayout>[0]['items']>(() => {
    if (selectedTab === Tabs.Contacts) {
      return members.map(member => ({
        avatar: {
          color: member.primaryColour?.hex || undefined,
          name: member.name || '',
          url: member.image?.url || undefined,
        },
        bottomLine: member.email,
        href: getUrl(IDENTIFIERS.DASHBOARD_MEMBER, { id: member.id }),
        id: member.id,

        isSelected: member.id === selectedId,
        label: member.name || 'No name',
      }));
    }

    if (selectedTab === Tabs.Companies) {
      return companies.map(company => ({
        avatar: {
          color: company.color || '',
          name: company.name || '',
          url: undefined,
        },
        bottomLine: company.email,
        href: getUrl(IDENTIFIERS.DASHBOARD_COMPANY, { id: company.id }),
        id: company.id,
        isSelected: company.id === selectedId,
        label: company.name || 'No name',
      }));
    }

    return [];
  }, [members, companies, selectedId, selectedTab]);

  const title = selectedTab === Tabs.Contacts ? 'Contacts' : 'Companies';

  const emptyStateText = selectedTab === Tabs.Contacts ? 'No Contacts Yet' : 'No Companies Yet';

  let itemsCount;

  if (selectedTab === Tabs.Contacts) {
    itemsCount = numMembers && numMembers > 0 ? numMembers : undefined;
  } else {
    itemsCount = numCompanies && numCompanies > 0 ? numCompanies : undefined;
  }

  const actions = [
    {
      icon: Plus,
      isPrimary: true,
      label: selectedTab === Tabs.Contacts ? 'Create Contact' : 'Create Company',
      onClick: () => {
        if (selectedTab === Tabs.Contacts) {
          setIsCreateContactModalOpen(true);
        } else {
          setIsCreateCompanyModalOpen(true);
        }
      },
    },
  ];

  const filters = useMemo(() => {
    if (selectedTab === Tabs.Contacts) {
      return {
        items: [
          {
            isMultiSelect: false as const,
            items: CONTACT_FILTERS.map(filter => ({
              isChecked: selectedFilter === filter.key,
              key: filter.key,
              label: filter.label,
            })),
            label: 'Type',
            onChange: (selected: string) => setSelectedFilter(selected as FilterType),
            selected: selectedFilter,
          },
        ],
      };
    }

    return undefined;
  }, [selectedTab, selectedFilter]);

  const getDetailPagePath = (id: string): string =>
    selectedTab === Tabs.Contacts ? getUrl(IDENTIFIERS.DASHBOARD_MEMBER, { id }) : getUrl(IDENTIFIERS.DASHBOARD_COMPANY, { id });

  return (
    <>
      <ListAndDetailsSubLayout
        header={{
          actions,
          tabs: [
            {
              label: 'Contacts',
              onClick: () => {
                setSelectedTab(Tabs.Contacts);
                router.push(getUrl(IDENTIFIERS.DASHBOARD_MEMBERS));
                setKey(prevState => prevState + 1);
              },
              selected: selectedTab === Tabs.Contacts,
            },
            {
              label: 'Companies',
              onClick: () => {
                setSelectedTab(Tabs.Companies);
                router.push(getUrl(IDENTIFIERS.DASHBOARD_COMPANIES));
                setKey(prevState => prevState + 1);
              },
              selected: selectedTab === Tabs.Companies,
            },
          ],
          title,
        }}
        searchKey={key}
        filters={filters}
        items={items}
        itemsCount={itemsCount}
        selectedId={selectedId}
        ifNoItemsText={emptyStateText}
        isFetchingList={isFetching}
        isOneColumn={isOneColumn}
        getDetailPagePath={({ id }) => getDetailPagePath(id)}
        onSearch={setSearchTerms}
        resourceType={'members'}
        pagination={{
          numPages,
          onChangePage: handlePageChange,
          page: currentPage,
        }}
      >
        {children}
      </ListAndDetailsSubLayout>

      {isCreateContactModalOpen && <CreateContactModal onClose={() => setIsCreateContactModalOpen(false)} />}

      {isCreateCompanyModalOpen && <CreateCompanyModal onClose={() => setIsCreateCompanyModalOpen(false)} />}
    </>
  );
};

export default Members;
